import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { chromeFocusRing } from './MenuItem';
import { NavBadge, NavItemLabel } from './NavBadge';
import { activeNavTo, allNav, mainNav, utilityNav, type NavBadges, type NavItem } from './nav';

function RailItem({
  item,
  active,
  disabled,
  count,
}: {
  item: NavItem;
  active: boolean;
  disabled: boolean;
  count: number;
}) {
  const Icon = item.icon;
  const body = (
    <>
      <span className="relative">
        <Icon
          className="h-[calc(24*var(--nav-px))] w-[calc(24*var(--nav-px))]"
          strokeWidth={2}
          aria-hidden
        />
        {item.badge && (
          <NavBadge
            kind={item.badge}
            count={count}
            className="absolute -right-[calc(10*var(--nav-px))] -top-[calc(6*var(--nav-px))]"
          />
        )}
      </span>
      <NavItemLabel item={item} count={count} short className="max-w-full truncate" />
    </>
  );
  // 72px wide and at least 58px tall, with an 11px label.
  const shape =
    'flex min-h-[calc(58*var(--nav-px))] w-[calc(72*var(--nav-px))] shrink-0 flex-col items-center justify-center gap-[calc(4*var(--nav-px))] rounded-[calc(12*var(--nav-px))] px-[calc(4*var(--nav-px))] text-center text-[length:calc(11*var(--nav-px))] leading-tight';

  if (disabled) {
    // As in the sidebar: shown, but inert until a workspace exists.
    return (
      <span
        aria-disabled="true"
        tabIndex={-1}
        className={cn(shape, 'pointer-events-none font-medium text-white/35')}
      >
        {body}
      </span>
    );
  }
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        shape,
        'transition-colors',
        active
          ? 'bg-white/[.14] font-semibold text-white'
          : 'font-medium text-sidebar-text hover:bg-white/10 hover:text-white',
        chromeFocusRing,
      )}
    >
      {body}
    </Link>
  );
}

/**
 * The tablet navigation (`md` to `lg`): an 88px rail under the top bar in the sidebar's green, holding
 * every sidebar item at once, so nothing hides behind a menu. New Content (`create`) is at the top; then
 * the main group, a divider, and the Configurations group, each in sidebar order with its short
 * label. On a screen too short for all of them the items scroll and New Content stays put.
 *
 * Drawn at the spec's exact sizes, NOT at the 80% the rest of the UI takes from 768 to 1365px (index.css):
 * at 80% its labels were 8.8px. The rail exists only from 768 to 1023px, always under that rule, so it
 * sets `--nav-px` to one CSS px at the 12.8px root (as `--touch-target` is 44px there) and writes every
 * size as so many of it: `calc(88*var(--nav-px))` is 88px. New Content and the badges inside it follow.
 *
 * It and the sidebar are both the "Primary" landmark; CSS shows one per width (`display: none`
 * takes the other out of the accessibility tree). `z-[45]`: over page content (which stacks up to
 * z-40) so New Content's menu and dimmed layer cover it, under the top bar (whose menus dim this).
 */
export function NavRail({
  disabled = false,
  create,
  badges,
}: {
  disabled?: boolean;
  create?: ReactNode;
  badges: NavBadges;
}) {
  const { pathname, search } = useLocation();
  const activeTo = activeNavTo(allNav, pathname, search);
  const row = (item: NavItem) => (
    <RailItem
      key={item.to}
      item={item}
      active={item.to === activeTo}
      disabled={disabled}
      count={item.badge ? badges[item.badge] : 0}
    />
  );

  return (
    <nav
      aria-label="Primary"
      aria-disabled={disabled || undefined}
      className="relative z-[45] hidden w-[calc(88*var(--nav-px))] shrink-0 flex-col items-center bg-gradient-to-b from-sidebar-from via-sidebar-via to-sidebar-to pt-[calc(14*var(--nav-px))] [--nav-px:0.078125rem] [color-scheme:dark] md:flex lg:hidden"
    >
      {create}
      {/* Reaches the rail's edges and starts 2px down: it clips, and the items' focus ring sits outside them. */}
      <div className="flex min-h-0 w-full flex-1 flex-col items-center gap-[calc(2*var(--nav-px))] overflow-y-auto pb-[calc(14*var(--nav-px))] pt-[calc(2*var(--nav-px))]">
        {mainNav.map(row)}
        <div
          aria-hidden
          className="my-[calc(8*var(--nav-px))] h-px w-[calc(48*var(--nav-px))] shrink-0 bg-white/[.14]"
        />
        {utilityNav.map(row)}
      </div>
    </nav>
  );
}
